import { PageProps } from '../../shell/shell';
import { TripBoxes } from '../../components/tripBoxes';
import React, { useCallback, useRef, useState } from 'react';
import { CreateTripModal } from './createTripModal';
import {
  useGetTripsQuery,
  usePostUploadsProfilePictureMutation,
  usePutProfilesByUserIdMutation,
} from '../../store/backendApi';
import { fullImage, imgUrl } from '../../utils/images';
import { LoadingChildrenBox } from '../../components/loadingChildrenBox';
import { LoadingChildren } from '../../components/loadingChildren';
import { LoadingTripBoxes } from '../../components/loadingTripBoxes';
import { InfoAlert } from '../../components/alerts/info';
import { SuccessAlert } from '../../components/alerts/success';
import { ErrorAlert } from '../../components/alerts/error';
import { CenteredInput } from '../../components/inputs/noLabel/centeredInput';
import { trackWithPlausible } from '../../utils/plausible';

export const MyTripsPage = React.memo((props: PageProps) => {
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const {
    data: tripsData,
    isLoading: areTripsLoading,
    error: tripsError,
  } = useGetTripsQuery('admin');
  const [changeProfilePicSuccessful, setChangeProfilePicSuccessful] = useState(false);
  const [changeProfilePicFailed, setChangeProfilePicFailed] = useState(false);
  const [changeProfilePicLoading, setChangeProfilePicLoading] = useState(false);
  const [generateUploadUrl] = usePostUploadsProfilePictureMutation();
  const [username, setUsername] = useState<string | undefined>(undefined);
  const [editUsername, setEditUsername] = useState(false);
  const [updateProfile, { isLoading: usernameUpdating }] = usePutProfilesByUserIdMutation();
  const imgRef = useRef<HTMLImageElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const profilePicUrl = props.user
    ? `https://media.${process.env.REACT_APP_HOST}/users/${
      props.user.userId
    }-thumbnail.jpg?v=${Date.now()}`
    : 'default-profile-picture.jpg';

  const uploadProfilePic = async (file: File) => {
    const getUploadUriResponse = await generateUploadUrl({
      filename: file.name,
    }).unwrap();
    const presignedUrls = getUploadUriResponse!;
    const uploadUrl = presignedUrls.uploadUrls[0].uploadUrl;

    const response = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
      },
    });
    if (!response.ok) {
      throw new Error(`upload failed with code ${response.status}`);
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setChangeProfilePicLoading(true);
    try {
      await uploadProfilePic(file);
      if (imgRef.current) {
        imgRef.current.src = '/spinner.gif';
        setTimeout(() => {
          imgRef.current!.src = `${profilePicUrl}?v=${Date.now()}`;
          setChangeProfilePicSuccessful(true);
        }, 5000);
      }
    } catch (e) {
      setChangeProfilePicFailed(true);
    } finally {
      setChangeProfilePicLoading(false);
    }
  };

  const handleCreateBoxClick = useCallback(() => {
    trackWithPlausible('trip-creation-initiated');
    setCreateModalOpen(true);
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-6">
      {changeProfilePicLoading && <InfoAlert text="Uploading..." />}
      {changeProfilePicSuccessful && <SuccessAlert text="Picture updated!" />}
      {changeProfilePicFailed && <ErrorAlert text="Picture upload failed" />}
      <LoadingChildrenBox
        loading={!props.user}
        loadingElement={
          <div className="rounded-full object-cover h-28 w-28 m-auto animate-pulse bg-gray-300" />
        }
      >
        <div className="size-28 m-auto">
          <div className="relative group w-full">
            <img
              ref={imgRef}
              id="profile-image"
              alt=""
              src={profilePicUrl}
              className="rounded-full object-cover h-28 w-28 m-auto"
              onError={(e) => (e.currentTarget.src = '/default-profile-picture.jpg')}
            />
            <div
              className="absolute inset-0 bg-gray-800 bg-opacity-50 flex items-center
                justify-center text-white text-lg font-semibold opacity-0 group-hover:opacity-100
                transition-opacity cursor-pointer rounded-full text-center"
              onClick={() => fileInputRef.current?.click()}
            >
              Change Picture
            </div>
          </div>
        </div>
      </LoadingChildrenBox>
      <div className="relative flex items-center justify-center mt-1">
        <LoadingChildren
          expectedChars={10}
          loadingClassName="mt-1 text-2xl/7 m-auto font-extrabold"
          loading={!props.user}
        >
          <div>
            <CenteredInput
              id={'username'}
              name={'username'}
              type={'text'}
              placeholder={'username'}
              value={username}
              disabled={!editUsername || usernameUpdating}
              defaultValue={props.user?.username ?? ''}
              setValue={setUsername}
              button={{
                text: editUsername ? 'Save' : usernameUpdating ? '...' : 'Edit',
                cancelText: editUsername ? 'Cancel' : undefined,
                cancelClick: () => {
                  setEditUsername(false);
                  setUsername(props.user?.username);
                },
                onClick: async () => {
                  if (!editUsername) {
                    setEditUsername(true);
                    return;
                  }
                  await updateProfile({
                    userId: props.user!.userId,
                    profileBodyRequest: {
                      username: username!,
                    },
                  });
                  setEditUsername(false);
                },
              }}
            />
          </div>
        </LoadingChildren>
      </div>
      <div className="mt-1 flex items-center justify-center">
        <LoadingChildren
          expectedChars={12}
          loadingClassName="mt-1 text-md text-center m-auto"
          loading={!tripsData}
        >
          <p className="mt-1 text-md text-center sm:w-[50%] m-auto">No profile description.</p>
        </LoadingChildren>
      </div>
      <div className="mt-16 w-full">
        {tripsData && (
          <TripBoxes
            trips={tripsData.map((trip) => tripToProp(trip))}
            useReactNavigation={true}
            createBoxClick={handleCreateBoxClick}
          />
        )}
        {!tripsData && <LoadingTripBoxes />}
      </div>
      <input
        type="file"
        accept="image/jpeg,image/jpg,image/png"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
      <CreateTripModal open={createModalOpen} setOpen={setCreateModalOpen} />
    </div>
  );
});

const tripToProp = (trip: any) => {
  return {
    title: trip.title,
    href: `/trips/${trip.id}`,
    imageUrl: trip.headerFileName
      ? imgUrl(trip.id, fullImage(trip.headerFileName))
      : '/default-trip-header.jpg',
    startDate: trip.startDate,
    endDate: trip.endDate,
    count: `${trip.postCount} ${trip.postCount === 1 ? 'post' : 'posts'}`,
    ownerId: trip.ownerId,
  };
};
